// begin_checkout/purchase can't run in the theme; they're in shopify-custom-pixel.js.
// Product cards are read live since swatches and API hydration swap their product in place.

const LIST_SELECTOR = "[data-analytics-list]";
const ITEM_SELECTOR = "[data-analytics-item], [data-product-card]";

// GA4 ignores items past 200 in one event.
const MAX_ITEMS_PER_EVENT = 200;

// Lists re-render and regroup in bursts (the collection grid especially), so
// wait for them to settle before reading which items are showing.
const SETTLE_DELAY = 750;

const roundMoney = (amount) => Math.round(amount * 100) / 100;

const defaultCurrency = () => window.Shopify?.currency?.active;

export function pushEcommerceEvent(event, ecommerce) {
  window.dataLayer = window.dataLayer || [];

  // Clear the previous event's ecommerce object so its items don't merge
  // into this one.
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event, ecommerce });
}

// Shopify line item (a /cart/add.js or /cart.js line) → GA4 item.
function lineItemToAnalyticsItem(line) {
  const item = {
    item_id: String(line.product_id),
    item_name: String(line.product_title || "")
      .split(" | ")[0]
      .trim(),
    price: roundMoney((line.final_price ?? line.price ?? 0) / 100),
  };

  if (line.vendor) item.item_brand = line.vendor;
  if (line.product_type) item.item_category = line.product_type;
  if (line.variant_title) item.item_variant = line.variant_title;

  return item;
}

// Quantity comes from requestedItems: the response's quantity is the line's cart total.
export function trackAddToCart(requestedItems, addedLines = [], currency = defaultCurrency()) {
  const items = addedLines.map((line) => {
    const requested = requestedItems.find((item) => String(item.id) === String(line.variant_id ?? line.id));

    return {
      ...lineItemToAnalyticsItem(line),
      quantity: Number.parseInt(requested?.quantity, 10) || 1,
    };
  });

  if (!items.length) return;

  pushEcommerceEvent("add_to_cart", {
    currency,
    value: roundMoney(items.reduce((total, item) => total + item.price * item.quantity, 0)),
    items,
  });
}

function readJson(element) {
  try {
    return JSON.parse(element.textContent);
  } catch {
    return null;
  }
}

function readItem(element) {
  if (element.dataset.analyticsItem) {
    try {
      return JSON.parse(element.dataset.analyticsItem);
    } catch {
      return null;
    }
  }

  const itemId = (element.dataset.productId || "").split("/").pop();

  if (!itemId) return null;

  const item = {
    item_id: itemId,
    item_name: element.querySelector("[data-product-card-title]")?.textContent.trim() || "",
  };

  if (element.dataset.productType) item.item_category = element.dataset.productType;

  const price = Number.parseFloat(element.querySelector("price-snippet")?.getAttribute("price"));

  if (Number.isFinite(price)) item.price = price;

  return item;
}

// Hidden cards (other colorways, grouped duplicates, paged-out or
// not-yet-revealed grid cards) aren't part of what the shopper sees. Slides
// scrolled off the side of a carousel are, and still count.
function isShowing(element) {
  return element.getClientRects().length > 0 && !element.closest("[data-pending-reveal]");
}

const viewedLists = new Set();
const reportedItemIds = new WeakMap();

function reportList(list) {
  if (!list.isConnected) {
    viewedLists.delete(list);

    return;
  }

  const itemListName = list.dataset.analyticsList;
  const itemListId = list.dataset.analyticsListId || window.handleize(itemListName);

  if (!reportedItemIds.has(list)) {
    reportedItemIds.set(list, new Set());
  }

  const reported = reportedItemIds.get(list);

  const items = [...list.querySelectorAll(ITEM_SELECTOR)]
    .filter((element) => element.closest(LIST_SELECTOR) === list && isShowing(element))
    .map((element, index) => ({ item: readItem(element), index }))
    .filter(({ item }) => item && !reported.has(item.item_id))
    .map(({ item, index }) => {
      reported.add(item.item_id);

      return { ...item, index, item_list_id: itemListId, item_list_name: itemListName };
    });

  for (let start = 0; start < items.length; start += MAX_ITEMS_PER_EVENT) {
    pushEcommerceEvent("view_item_list", {
      currency: defaultCurrency(),
      item_list_id: itemListId,
      item_list_name: itemListName,
      items: items.slice(start, start + MAX_ITEMS_PER_EVENT),
    });
  }
}

let settleTimer = null;

function scheduleReport() {
  window.clearTimeout(settleTimer);

  settleTimer = window.setTimeout(() => {
    viewedLists.forEach(reportList);
  }, SETTLE_DELAY);
}

const listObserver = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (!entry.isIntersecting) return;

    viewedLists.add(entry.target);
    listObserver.unobserve(entry.target);
  });

  scheduleReport();
});

const observedLists = new WeakSet();

function observeLists() {
  document.querySelectorAll(LIST_SELECTOR).forEach((list) => {
    if (observedLists.has(list)) return;

    observedLists.add(list);
    listObserver.observe(list);
  });
}

function trackViewItem(root = document) {
  const data = root?.querySelector("[data-analytics-view-item]");
  const ecommerce = data && readJson(data);

  if (ecommerce) {
    pushEcommerceEvent("view_item", ecommerce);
  }
}

function trackSearch() {
  const data = document.querySelector("[data-analytics-search]");
  const searchTerm = data && readJson(data)?.search_term;

  if (!searchTerm) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: "search", search_term: searchTerm });
}

function init() {
  trackSearch();
  trackViewItem();
  observeLists();

  // Catches lists/items rendered after load (recommendations, filtering, load more, PDP color swap).
  new MutationObserver(() => {
    observeLists();

    if (viewedLists.size) {
      scheduleReport();
    }
  }).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["hidden", "data-pending-reveal"],
  });

  // A color change on the PDP swaps in the other product's section without a
  // page load — that's a new product view.
  document.addEventListener("product:ajax-rendered", (event) => {
    trackViewItem(event.detail?.section);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
